import React, { createContext, useContext, useState } from "react";
import ParameterValue from "../api/models/test-series/ParameterValue";
import Parameter from "../api/models/test-series/Parameter";
import { SimpleTextDialogProps } from "../components/Dialog/SimpleTextDialog";
import { ParameterListDialogProps } from "../components/Dialog/ParameterListDialog/ParameterListDialog";
import NumberParameter from "../api/models/test-series/NumberParameter";
import ActionTemplate from "../api/models/test-series/ActionTemplate";
import TestPlanPermissionContext from "./TestPlanPermissionContext";
import Section from "../api/models/test-series/Section";
import { TestSeriesPermissions } from "../api/services/PermissionApi";
import TestSeriesSearchResult from "../api/models/test-series/TestSeriesSearchResult";
import ActionValueTableRow from "../api/models/test-series/ActionValueTableRow";
import TestSeriesVersionReferenceResponse from "../api/models/test-series/TestSeriesVersionReferenceResponse";
import TestSeriesDTO from "../api/models/TestSeriesDTO";
import TestPlanResponse from "../api/models/test-series/TestPlanResponse";

export type RegainConfig = {
  originalTestSeriesId: number;
  regainConfig: "BOTH" | "PRODUCTS" | "SUBSTRATES";
  regainFirstSubstrateSection?: boolean;
  regainSecondSubstrateSection?: boolean;
} | null;

export enum TestSeriesTabType {
  TestSeriesConfigurator = 0,
  TestTable = 1,
}

interface TestSeriesContextState {
  openTabValue: number;
  originRequestId: string;
  updateOriginRequestId: (requestId: string) => void;
  parameterListDialogState: any;
  selectedTestSeriesId: number | unknown;
  selectedTestSeriesHasTestCases: boolean;
  selectedTestSeriesObject: TestSeriesDTO;
  permissions: TestSeriesPermissions;
  testPlans: TestPlanResponse[];
  setTestPlans: (testPlans: TestPlanResponse[]) => void;
  showParameterListDialog: (
    title: string,
    parameters: Parameter[],
    parameterValues: ParameterValue[],
    hasUncontrolledParameters: boolean,
    additionalProps?: {
      hintText?: string;
      disabled?: boolean;
      unitType?: string;
      fetchActionTemplates?: () => Promise<ActionTemplate[]>;
      rows?: ActionValueTableRow[];
      rowParameters?: Parameter[];
    },
  ) => Promise<{ values: ParameterValue[]; rows?: ActionValueTableRow[] }>;
  showTextDialog: (
    title: string,
    value?: string,
    label?: string,
    additionalProps?: {
      disabledOnEmpty?: boolean;
      disabledHint?: string;
    },
  ) => Promise<string>;
  testMethod: unknown;
  isMethodDialogOpen: boolean;
  isSpecificationDialogOpen: boolean;
  regainConfig: RegainConfig;
  testSeries: TestSeriesSearchResult[];
  textDialog: SimpleTextDialogProps;
  updateOpenTabValue: (val: TestSeriesTabType) => void;
  updateSelectedTestSeriesId: (testSeriesId: unknown) => void;
  updateSelectedTestSeriesObject: (testSeriesObject: TestSeriesDTO) => void;
  resetSelectedTestSeriesObject: () => void;
  updateSelectedTestSeriesHasTestCases: (hasTestCases: boolean) => void;
  updateTestMethod: (testMethod: unknown) => void;
  setIsMethodDialogOpen: (isMethodDialogOpen: boolean) => void;
  setIsSpecificationDialogOpen: (isSpecificationDialogOpen: boolean) => void;
  updateRegainConfig: (regainConfig: RegainConfig) => void;
  updateTestSeries: (testSeries: TestSeriesSearchResult[]) => void;
  testSeriesCurrentVersion: number;
  testSeriesLatestVersion: number;
  updateTestSeriesCurrentVersion: (version: number) => void;
  testSeriesIsReused: boolean;
  updateTestSeriesIsReused: (reused: boolean) => void;
}

const HIDE_PARAMETER_LIST_DIALOG: ParameterListDialogProps = {
  onSave: (): void => {},
  onClose: (): void => {},
  title: "",
  open: false,
  parameters: [],
  parameterValues: [],
};

const HIDE_TEXT_DIALOG: SimpleTextDialogProps = {
  onSave: (): void => {},
  onClose: (): void => {},
  title: "",
  open: false,
};

const EMPTY_SECTION: Section = {
  id: 0,
  name: "",
  description: "",
};

const EMPTY_TEST_SERIES: TestSeriesDTO = {
  id: 0,
  indexNumber: 1,
  modified: false,
  linkedTestSeriesChild: null,
  linkedTestSeriesParent: null,
  name: "",
  note: null,
  conditionSection: {
    ...EMPTY_SECTION,
    conditions: [],
    parametersInAllTests: [],
  },
  measurementSection: {
    ...EMPTY_SECTION,
    measurements: [],
  },
  productSection: {
    ...EMPTY_SECTION,
    actionsOnAllProducts: null,
    productActionFlows: [],
  },
  specimenSection: {
    ...EMPTY_SECTION,
    buildingActions: [],
    buildingActionsOnAllSpecimen: null,
    treatments: [],
    treatmentOnAllSpecimen: null,
  },
  substrateSections: [],
  testSeriesStatus: "DRAFT",
  testCasesCount: 0,
  testCasesGenerated: false,
  testClassReference: {},
  testLocation: "CSC_LAB",
  testMethodReference: {},
  version: 1,
  versions: [],
};

function findLatestVersion(versionRefs: TestSeriesVersionReferenceResponse[]) {
  return versionRefs.reduce<number>((max, curVr) => {
    const { version } = curVr;
    if (version > max) return version;
    return max;
  }, 1);
}

function usePermissions(selectedTestSeries: TestSeriesDTO, isReused: boolean, isLatestVersion: boolean) {
  const {
    testSeries: currentTestSeriesPermissions,
    nonCurrentTestSeries: nonCurrentTestSeriesPermissions,
    reusedTestSeries: reusedTestSeriesPermissions,
  } = useContext(TestPlanPermissionContext);
  if (isReused) {
    return reusedTestSeriesPermissions || {};
  }
  return isLatestVersion
    ? currentTestSeriesPermissions?.[String(selectedTestSeries.id)] || {}
    : nonCurrentTestSeriesPermissions || {};
}

export const TestSeriesContext = createContext<TestSeriesContextState>({
  openTabValue: 0,
  originRequestId: "",
  updateOriginRequestId: () => {},
  parameterListDialogState: HIDE_PARAMETER_LIST_DIALOG,
  selectedTestSeriesId: undefined,
  selectedTestSeriesHasTestCases: false,
  selectedTestSeriesObject: EMPTY_TEST_SERIES,
  permissions: {},
  testPlans: [],
  setTestPlans: () => {},
  showParameterListDialog: () => Promise.reject(),
  showTextDialog: () => Promise.reject(),
  testMethod: undefined,
  isMethodDialogOpen: false,
  isSpecificationDialogOpen: false,
  regainConfig: null,
  testSeries: [],
  textDialog: HIDE_TEXT_DIALOG,
  updateOpenTabValue: () => {},
  updateSelectedTestSeriesId: () => {},
  updateSelectedTestSeriesObject: () => {},
  resetSelectedTestSeriesObject: () => {},
  updateSelectedTestSeriesHasTestCases: () => {},
  updateTestMethod: () => {},
  setIsMethodDialogOpen: () => {},
  setIsSpecificationDialogOpen: () => {},
  updateRegainConfig: () => {},
  updateTestSeries: () => {},
  testSeriesLatestVersion: 1,
  testSeriesCurrentVersion: 1,
  updateTestSeriesCurrentVersion: () => {},
  testSeriesIsReused: false,
  updateTestSeriesIsReused: () => {},
});

const TestSeriesProvider: React.FunctionComponent<{
  children?: JSX.Element;
}> = (props) => {
  const [isMethodDialogOpen, setIsMethodDialogOpen] = useState(false);
  const [isSpecificationDialogOpen, setIsSpecificationDialogOpen] = useState(false);
  const [selectedTestSeriesHasTestCases, setSelectedTestSeriesHasTestCases] = useState(false);
  const [testMethod, setTestMethod] = useState<unknown>(undefined);
  const [regainConfig, updateRegainConfig] = useState<RegainConfig>(null);
  const [testSeries, setTestSeries] = useState<TestSeriesSearchResult[]>([]);
  const [parameterListDialogState, setParameterListDialogState] = useState(HIDE_PARAMETER_LIST_DIALOG);
  const [selectedTestSeriesId, setSelectedTestSeriesId] = useState<unknown>(undefined);
  const [selectedTestSeriesObject, setSelectedTestSeriesObject] = useState<TestSeriesDTO>(EMPTY_TEST_SERIES);
  const [testSeriesCurrentVersion, setTestSeriesCurrentVersion] = useState(1);
  const [testSeriesIsReused, setTestSeriesIsReused] = useState(false);
  const [originRequestId, setOriginRequestId] = useState("");
  const [openTabValue, setOpenTabValue] = useState(0);
  const [testPlans, setTestPlans] = useState<TestPlanResponse[]>([]);
  const permissions = usePermissions(
    selectedTestSeriesObject,
    testSeriesIsReused,
    findLatestVersion(selectedTestSeriesObject.versions) === testSeriesCurrentVersion,
  );

  const updateOpenTabValue = (val: TestSeriesTabType) => {
    setOpenTabValue(val);
  };

  const updateTestSeries = (newTestSeries: TestSeriesSearchResult[]): void => {
    setTestSeries(newTestSeries);
  };

  const updateSelectedTestSeriesId = (testSeriesId: unknown): void => {
    setSelectedTestSeriesId(testSeriesId);
  };

  const resetSelectedTestSeriesObject = (): void => {
    setSelectedTestSeriesObject(EMPTY_TEST_SERIES);
  };

  const updateSelectedTestSeriesObject = (testSeriesObject: TestSeriesDTO): void => {
    setSelectedTestSeriesObject(testSeriesObject);
  };

  const updateSelectedTestSeriesHasTestCases = (hasTestCases: boolean): void => {
    setSelectedTestSeriesHasTestCases(hasTestCases);
  };

  const updateTestSeriesCurrentVersion = (version: number) => {
    setTestSeriesCurrentVersion(version);
  };

  const updateTestSeriesIsReused = (reused: boolean) => {
    setTestSeriesIsReused(reused);
  };

  const updateOriginRequestId = (requestId: string) => {
    setOriginRequestId(requestId);
  };

  const [textDialog, setTextDialog] = useState(HIDE_TEXT_DIALOG);

  const updateTestMethod = (newTestMethod: unknown): void => {
    setTestMethod(newTestMethod);
  };

  const findSharedUnitParams = (unitType: string, selectedParam: Parameter): ParameterValue[] => {
    const measurements = selectedTestSeriesObject?.measurementSection.measurements;
    return measurements.filter(
      (m) =>
        m.parameter.type === "NUMBER" &&
        (m.parameter as NumberParameter).unit.type === unitType &&
        m.parameter.tmbId !== selectedParam.tmbId,
    );
  };

  const showParameterListDialog = (
    title: string,
    parameters: Parameter[],
    parameterValues: ParameterValue[],
    hasUncontrolledParameters: boolean,
    additionalProps: {
      hintText?: string;
      disabled?: boolean;
      unitType?: string;
      fetchActionTemplates?: () => Promise<ActionTemplate[]>;
    } = {},
  ): Promise<{ values: ParameterValue[]; rows?: ActionValueTableRow[] }> => {
    if (parameterListDialogState.open) {
      parameterListDialogState.onClose();
    }

    const { unitType } = additionalProps;
    const sharedUnitParams = unitType ? findSharedUnitParams(unitType, parameters[0]) : undefined;

    return new Promise((resolve, reject) => {
      setParameterListDialogState({
        open: true,
        hasUncontrolledParameters,
        title,
        parameters,
        parameterValues,
        ...additionalProps,
        onClose: () => {
          reject();
          setParameterListDialogState(HIDE_PARAMETER_LIST_DIALOG);
        },
        onSave: (values: any, rows?: ActionValueTableRow[]) => {
          resolve({ values, rows });
          setParameterListDialogState(HIDE_PARAMETER_LIST_DIALOG);
        },
        sharedUnitParams,
      });
    });
  };

  const showTextDialog = (
    title: string,
    value?: string,
    label?: string,
    additionalProps: {
      disabledOnEmpty?: boolean;
      disabledHint?: string;
    } = {},
  ): Promise<string> => {
    if (textDialog.open) {
      textDialog.onClose();
    }
    return new Promise((resolve, reject) => {
      setTextDialog({
        open: true,
        title,
        label,
        value,
        ...additionalProps,
        onClose: () => {
          reject();
          setTextDialog(HIDE_TEXT_DIALOG);
        },
        onSave: (textValue) => {
          resolve(textValue);
          setTextDialog(HIDE_TEXT_DIALOG);
        },
      });
    });
  };

  const testSeriesLatestVersion = findLatestVersion(selectedTestSeriesObject.versions);

  const { children } = props;

  return (
    <TestSeriesContext.Provider
      value={{
        openTabValue,
        originRequestId,
        updateOriginRequestId,
        parameterListDialogState,
        selectedTestSeriesId,
        selectedTestSeriesObject,
        selectedTestSeriesHasTestCases,
        permissions,
        testPlans,
        setTestPlans,
        showParameterListDialog,
        showTextDialog,
        testMethod,
        isMethodDialogOpen,
        isSpecificationDialogOpen,
        regainConfig,
        testSeries,
        textDialog,
        updateOpenTabValue,
        updateSelectedTestSeriesId,
        updateSelectedTestSeriesObject,
        resetSelectedTestSeriesObject,
        updateSelectedTestSeriesHasTestCases,
        updateTestMethod,
        setIsMethodDialogOpen,
        setIsSpecificationDialogOpen,
        updateRegainConfig,
        updateTestSeries,
        testSeriesLatestVersion,
        testSeriesCurrentVersion,
        updateTestSeriesCurrentVersion,
        testSeriesIsReused,
        updateTestSeriesIsReused,
      }}
    >
      {children}
    </TestSeriesContext.Provider>
  );
};

export default TestSeriesProvider;
